import { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import logo from './logo.svg';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiError } from './api/client';
import { AuthProvider, useAuth } from './auth/AuthContext';
import {
  IconAward,
  IconCalendar,
  IconClose,
  IconContact,
  IconLayoutGrid,
  IconKey,
  IconLogOut,
  IconMegaphone,
  IconMenu,
  IconRoute,
  IconShieldCheck,
  IconShuffle,
  IconSliders,
  IconTeams,
  IconTrendUp,
  IconUsers,
  type IconProps,
  IconMessage,
  IconSun,
  IconMoon,
} from './components/icons';
import { CredentialsPage } from './pages/CredentialsPage';
import { CustomFieldsPage } from './pages/CustomFieldsPage';
import { DashboardPage } from './pages/DashboardPage';
import { LeadDetailPage } from './pages/LeadDetailPage';
import { LeadRoutingPage } from './pages/LeadRoutingPage';
import { LoginActivityPage } from './pages/LoginActivityPage';
import { CampaignsPage } from './pages/CampaignsPage';
import { LeadStatusesPage } from './pages/LeadStatusesPage';
import { LeadsPage } from './pages/LeadsPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { LoginPage } from './pages/LoginPage';
import { MetaIntegrationPage } from './pages/MetaIntegrationPage';
import { MetaOAuthCallbackPage } from './pages/MetaOAuthCallbackPage';
import { RolesPage } from './pages/RolesPage';
import { TeamDistributionPage } from './pages/TeamDistributionPage';
import { TeamPerformancePage } from './pages/TeamPerformancePage';
import { TeamsPage } from './pages/TeamsPage';
import { UsersPage } from './pages/UsersPage';
import { UpdateBanner } from './components/UpdateBanner';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { PortalLinksPage } from './pages/PortalLinksPage';
import { WhatsAppPage } from './pages/WhatsAppPage';
import { WhatsAppIntegrationPage } from './pages/WhatsAppIntegrationPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // 4xx means the request was wrong, not unlucky — retrying just burns
        // time and can trigger a refresh loop on a 401.
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

function BrandMark() {
  return (
    <div className="brand-mark" style={{ background: 'transparent', width: 36, height: 36, padding: 0 }}>
      <img src={logo} alt="Sisya Logo" style={{ width: '100%', height: '100%' }} />
    </div>
  );
}

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<IconProps>;
  permission?: string;
  anyOf?: string[];
}

function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user, logout, can, canAny } = useAuth();
  const location = useLocation();

  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('crm-theme') || 'system';
  });

  useEffect(() => {
    if (theme === 'system') {
      document.documentElement.removeAttribute('data-theme');
      localStorage.removeItem('crm-theme');
    } else {
      document.documentElement.setAttribute('data-theme', theme);
      localStorage.setItem('crm-theme', theme);
    }
  }, [theme]);

  function toggleTheme() {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  }

  // Close the mobile drawer on every navigation so a tap on a link doesn't
  // leave the overlay stuck open behind the new page.
  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  if (!user) return null;

  const roleLabel =
    user.scope === 'all' ? 'Administrator' : user.scope === 'team' ? 'Manager' : 'Team Specialist';

  const workspace: NavItem[] = [
    { to: '/dashboard', label: 'Dashboard', icon: IconLayoutGrid },
    { to: '/leads', label: 'Leads', icon: IconContact },
    { to: '/whatsapp', label: 'WhatsApp', icon: IconMessage },
    { to: '/performance', label: 'Team performance', icon: IconTrendUp, permission: 'report.view' },
    { to: '/team-distribution', label: 'My team', icon: IconShuffle, permission: 'team.manage' },
  ];
  const admin: NavItem[] = [
    { to: '/settings/users', label: 'Users', icon: IconUsers, permission: 'user.manage' },
    { to: '/settings/login-activity', label: 'Login activity', icon: IconCalendar, permission: 'audit.view' },
    { to: '/settings/teams', label: 'Teams', icon: IconTeams, permission: 'user.manage' },
    { to: '/settings/roles', label: 'Roles & permissions', icon: IconShieldCheck, anyOf: ['role.manage', 'user.manage'] },
    { to: '/settings/lead-statuses', label: 'Lead statuses', icon: IconAward, permission: 'status.manage' },
    { to: '/settings/campaigns', label: 'Campaigns', icon: IconMegaphone, permission: 'campaign.manage' },
    { to: '/settings/custom-fields', label: 'Custom fields', icon: IconSliders, permission: 'custom_field.manage' },
    { to: '/settings/lead-routing', label: 'Lead routing', icon: IconRoute, permission: 'assignment_rule.manage' },
    { to: '/settings/integrations/meta', label: 'Meta Lead Ads', icon: IconMegaphone, permission: 'integration.manage' },
    { to: '/settings/integrations/whatsapp', label: 'WhatsApp', icon: IconMessage, permission: 'integration.manage' },
    { to: '/settings/credentials', label: 'API credentials', icon: IconKey, permission: 'api_key.manage' },
  ];

  const portal: NavItem[] = [
    { to: '/portals', label: 'Portal links', icon: IconLayoutGrid },
  ];

  const visible = (item: NavItem) => {
    if (item.to === '/performance' && user.scope !== 'all') return false;
    return item.permission ? can(item.permission) : item.anyOf ? canAny(...item.anyOf) : true;
  };

  const workspaceLinks = workspace.filter(visible);
  const adminLinks = admin.filter(visible);
  const portalLinks = user.scope === 'all' ? portal.filter(visible) : [];

  return (
    <>
      <div className="sidebar-backdrop" data-open={open} onClick={onClose} />
      <aside className="sidebar" data-open={open} aria-label="Primary navigation">
        <div className="sidebar-brand">
          <BrandMark />
          <div className="brand-text">
            <b>SISYA CRM</b>
            <span>{roleLabel}</span>
          </div>
        </div>

        <nav className="sidebar-nav">
          {workspaceLinks.length > 0 && <div className="sidebar-section">Workspace</div>}
          {workspaceLinks.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
            >
              <item.icon />
              {item.label}
            </NavLink>
          ))}
          <NavLink
            to="/change-password"
            className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
          >
            <IconKey />
            Change password
          </NavLink>

          {adminLinks.length > 0 && <div className="sidebar-section">Administration</div>}
          {adminLinks.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
            >
              <item.icon />
              {item.label}
            </NavLink>
          ))}

          {portalLinks.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
            >
              <item.icon />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div style={{ padding: '0 12px 16px' }}>
          <button 
            className="btn" 
            onClick={toggleTheme} 
            style={{ width: '100%', display: 'flex', justifyContent: 'center', background: 'var(--sb-hover)', color: 'var(--sb-ink)', border: '1px solid var(--sb-border)' }}
          >
            {theme === 'light' ? <IconMoon width={16} height={16} /> : <IconSun width={16} height={16} />}
            Switch to {theme === 'light' ? 'Night' : 'Light'} Mode
          </button>
        </div>

        <div className="sidebar-footer">
          <div className="sidebar-avatar" aria-hidden="true">{initials(user.full_name)}</div>
          <div className="sidebar-user">
            <b>{user.full_name}</b>
            <span>{user.email}</span>
          </div>
          <button className="sidebar-signout" onClick={logout} aria-label="Sign out" title="Sign out">
            <IconLogOut width={16} height={16} />
          </button>
        </div>
      </aside>
    </>
  );
}

/** Redirects to login when signed out, and away from pages the role can't use. */
function Protected({
  children,
  permission,
}: {
  children: React.ReactNode;
  permission?: string;
}) {
  const { user, loading, can } = useAuth();

  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <span className="spinner" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (permission && !can(permission)) return <Navigate to="/leads" replace />;
  return <>{children}</>;
}

function Routing() {
  const { user, loading } = useAuth();
  const [navOpen, setNavOpen] = useState(false);
  const landing = '/dashboard';

  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh' }}>
        <span className="spinner" />
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  return (
    <div className="app">
      <Sidebar open={navOpen} onClose={() => setNavOpen(false)} />

      <div className="main">
        <div className="mobile-topbar">
          <button className="btn btn-ghost btn-sm" onClick={() => setNavOpen((v) => !v)} aria-label="Toggle menu">
            {navOpen ? <IconClose width={18} height={18} /> : <IconMenu width={18} height={18} />}
          </button>
          <div className="brand-mark" style={{ width: 26, height: 26, borderRadius: 7 }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width={13} height={13}>
              <path d="M12 3.5 20 8v8l-8 4.5L4 16V8z" />
            </svg>
          </div>
          <b style={{ fontSize: 14 }}>Sisya CRM</b>
        </div>

        <Routes>
          <Route path="/login" element={<Navigate to={landing} replace />} />
          <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
          <Route path="/leads" element={<Protected><LeadsPage /></Protected>} />
          <Route path="/leads/:id" element={<Protected><LeadDetailPage /></Protected>} />
          <Route path="/whatsapp" element={<Protected><WhatsAppPage /></Protected>} />
          <Route path="/change-password" element={<Protected><ChangePasswordPage /></Protected>} />
          <Route path="/portals" element={<Protected><PortalLinksPage /></Protected>} />
          <Route
            path="/performance"
            element={
              <Protected permission="report.view">
                <TeamPerformancePage />
              </Protected>
            }
          />
          <Route
            path="/team-distribution"
            element={
              <Protected permission="team.manage">
                <TeamDistributionPage />
              </Protected>
            }
          />
          <Route
            path="/settings/users"
            element={
              <Protected permission="user.manage">
                <UsersPage />
              </Protected>
            }
          />
          <Route
            path="/settings/teams"
            element={
              <Protected permission="user.manage">
                <TeamsPage />
              </Protected>
            }
          />
          <Route
            path="/settings/roles"
            element={
              <Protected permission="user.manage">
                <RolesPage />
              </Protected>
            }
          />
          <Route
            path="/settings/login-activity"
            element={
              <Protected permission="audit.view">
                <LoginActivityPage />
              </Protected>
            }
          />
          <Route
            path="/settings/campaigns"
            element={
              <Protected permission="campaign.manage">
                <CampaignsPage />
              </Protected>
            }
          />
          <Route
            path="/settings/lead-statuses"
            element={
              <Protected permission="status.manage">
                <LeadStatusesPage />
              </Protected>
            }
          />
          <Route
            path="/settings/custom-fields"
            element={
              <Protected permission="custom_field.manage">
                <CustomFieldsPage />
              </Protected>
            }
          />
          <Route
            path="/settings/lead-routing"
            element={
              <Protected permission="assignment_rule.manage">
                <LeadRoutingPage />
              </Protected>
            }
          />
          <Route
            path="/settings/integrations/meta"
            element={
              <Protected permission="integration.manage">
                <MetaIntegrationPage />
              </Protected>
            }
          />
          <Route
            path="/settings/integrations/whatsapp"
            element={
              <Protected permission="integration.manage">
                <WhatsAppIntegrationPage />
              </Protected>
            }
          />
          {/* Facebook redirects here after login; the code is exchanged on arrival. */}
          <Route
            path="/settings/integrations/meta/callback"
            element={
              <Protected permission="integration.manage">
                <MetaOAuthCallbackPage />
              </Protected>
            }
          />
          {/* Any signed-in user may see their own key; only api_key.manage can issue one. */}
          <Route path="/settings/credentials" element={<Protected><CredentialsPage /></Protected>} />
          <Route path="/team" element={<Navigate to="/team-distribution" replace />} />
          <Route path="*" element={<Navigate to={landing} replace />} />
        </Routes>
      </div>
    </div>
  );
}

export function App() {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        return false;
      }
      if (e.key === 'PrintScreen') {
        navigator.clipboard.writeText('');
        e.preventDefault();
        return false;
      }
    }
    function handleKeyUp(e: KeyboardEvent) {
      if (e.key === 'PrintScreen') {
        navigator.clipboard.writeText('');
        e.preventDefault();
        return false;
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <UpdateBanner />
          <Routing />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
